import _ from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Col, Row, Table } from "reactstrap";
import { usePostStockStatusGridData } from "../hooks";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers } from "../utils";
import { DynamicInput, ServiceThreadSpinner, StockStatusGrid } from "./";

const fieldObjects = constants.StockStatusFieldObjects;

export default function InventoryStockStatus(props) {
   const [calldata, setCallData] = useState(false);
   const [classificationTotal, setClassificationTotal] = useState(0);
   const [classificationTotals, setClassificationTotals] = useState([]);
   const [data, setData] = useState({ locations: [], itemclasses: [], abcCodes: [] });
   const [downloading, setDownloading] = useState(false);
   const [fieldsComplete, setFieldsComplete] = useState(false);
   const [gridMode, setGridMode] = useState(0);
   const [loaded, setLoaded] = useState(false);
   const [refreshScreen, setRefreshscreen] = useState(false);
   const [reportTotal, setReportTotal] = useState(0);
   const [reportTotals, setReportTotals] = useState([]);
   const [resetting, setResetting] = useState(false);
   const [screenFields, setScreenFields] = useState(constants.InventoryStockStatusInitialState);
   const [stockStatusSortDirection, setStockStatusSortDirection] = useState("asc");
   const [stockStatusSortField, setStockStatusSortField] = useState("ItemWithDescription");
   const gridData = usePostStockStatusGridData(calldata, screenFields);

   useEffect(() => {
      document.title = `Inventory Stock Status`;
      if (resetting) return;
      if (!loaded) {
         LoadData();
      }
      if (!fieldsComplete && loaded && data.locations?.length && data.itemClasses?.length) {
         _.map(fieldObjects, (x) => {
            let key = x.label;
            x.value = screenFields[key]; // assign initial values
            if (x.type === constants.INPUT_TYPES.checkboxANDInput) {
               if (x.typeAhead) {
                  x.textValue = screenFields[`${x.label}Text`];
               } else {
                  x.textValue = "";
               }
            }
            if (x.type === constants.INPUT_TYPES.select) { // assign a list if a select
               x.options = data[key];
            }
            return x;
         });
         setFieldsComplete(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded, fieldsComplete, resetting]);

   useEffect(() => {
      calculateTotals();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [gridData]);

   const buttonFunctions = {
      reset: {
         name: "Reset",
         function: async () => {
            setCallData(false);
            setResetting(true);
            setStockStatusSortField("ItemWithDescription");
            setStockStatusSortDirection("asc");
            await new Promise((resolve) => {
               screenFields.itemNumber = true;
               screenFields.itemDescriptionPartial = false;
               screenFields.itemNumberText = { label: "--- All ---", value: "" };
               screenFields.itemDescriptionPartialText = "";
               screenFields.abcCodes = { label: "--- All ---", value: 0 };
               screenFields.itemClasses = [];
               screenFields.locations = [];
               screenFields.showZeroQty = false;
               screenFields.sortField = "ItemWithDescription";
               screenFields.sortDirection = "asc";
               screenFields.gridMode = 0;
               setFieldsComplete(false);
               resolve();
            }).finally(() => setResetting(false))
         },
      },
      viewData: {
         name: "View Data",
         function: () => {
            setGridMode(constants.StockStatusGridModes.viewData)
            setCallData(true);
            setScreenFields({
               ...screenFields,
               gridMode: constants.StockStatusGridModes.viewData
            });
         },
      },
      viewReport: {
         name: "View Report",
         function: () => {
            //if(!gridData?.length) return;  // This disables the button on initial page load. Jay didn't like it.
            setDownloading(true)
            api.postDownloadBinary(
               'Report/StockStatusReport',
               constants.GenViewDataPayLoad(screenFields),
               constants.MIME_PDF,
               `StockStatus_${dateHelpers.toMDYDateString(new Date())}.pdf`,
               "There was an error downloading this stock status pdf"
            )
               .then((res) => {
                  if (res?.data === false) {
                     toast.error(res.message);
                  }
               })
               .catch(api.catchHandler)
               .finally(() => setDownloading(false));
         },
         disable: downloading
      },
      editItems: {
         name: "Edit Item",
         function: () => {
            if (!screenFields.itemDescriptionPartialText?.length) {
               toast.error("Item Partial Text Needed!");
               return;
            }
            setGridMode(constants.StockStatusGridModes.edit)
            setCallData(true);
            setScreenFields({
               ...screenFields,
               gridMode: constants.StockStatusGridModes.edit
            });
         },
      },
   };

   function calculateTotals() {
      let itemTypeGroups = _.groupBy(gridData, x => x.classType);

      let itemTypes = [];
      _.forEach(itemTypeGroups, function (x, index) {
         let total = x.reduce((total, currentItem) => (total += currentItem.siteCost), 0);
         itemTypes.push({ label: index, value: total });
      });
      setClassificationTotals(itemTypes);

      let itemTotal = itemTypes.reduce((total, currentItem) => (total += currentItem.value), 0);
      setClassificationTotal(itemTotal);

      let locationGroups = _.groupBy(gridData, x => x.siteCode);

      let locationTypes = [];
      _.forEach(locationGroups, function (x, index) {
         let total = x.reduce((total, currentItem) => (total += currentItem.siteCost), 0);
         locationTypes.push({ label: index, value: total });
      });
      setReportTotals(locationTypes);

      let locationTotal = locationTypes.reduce((total, currentItem) => (total += currentItem.value), 0);
      setReportTotal(locationTotal);
   }

   const exportXlsx = () => {
      if (!gridData?.length) return;
      api.postGetBlob("Inventory/ExportStockStatusXLSX", constants.GenViewDataPayLoad(screenFields))
         .then(r => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               let XLSX_FILENAME = `StockStatus.xlsx`;
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_XLSX)
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
   };

   function LoadData() {
      api
         .fetch("Inventory/LoadStockStatusData")
         .then((response) => {
            if (response.data.success) {
               setData(response.data.stockStatusData);
            }
            setLoaded(true);
         })
         .catch(api.catchHandler);
   }

   const onChange = (fieldName, fieldValue, idx, texValue, isRegularCheckBox) => {
      if (fieldValue === null) {
         if (fieldName === "itemNumber" && fieldValue === null) {
            fieldObjects[idx].textValue = fieldValue;
            setScreenFields((prev) => {
               prev[`${fieldName}Text`] = "";
               return prev;
            });
            setRefreshscreen(!refreshScreen);
            // return;
         }
         if (isRegularCheckBox) {
            fieldObjects[idx].value = !fieldObjects[idx].value
            setScreenFields((prev) => {
               prev[fieldName] = !prev[fieldName];
               return prev;
            })
            setRefreshscreen(!refreshScreen);
            return;
         }
         if (fieldObjects[idx].value) return; //to set up alternating checkboxes where one is always true
         constants.flipCheckBox(idx);
         let oppositeChckBoxIdx = _.findIndex(
            constants.StockStatusFieldObjects,
            ["type", constants.INPUT_TYPES.checkboxANDInput] && ["value", true]
         );
         if (!constants.StockStatusFieldObjects[oppositeChckBoxIdx]?.typeAhead) {
            setScreenFields((prev) => {
               prev["itemNumberText"] = { label: "--- All ---", value: "" };
               return prev;
            })
         } else {
            setScreenFields((prev) => {
               prev["itemDescriptionPartialText"] = "";
               return prev;
            })
         }
      } else {
         if (texValue) {
            fieldObjects[idx].textValue = fieldValue;
            setScreenFields((prev) => {
               prev[`${fieldName}Text`] = fieldValue;
               return prev;
            });
         } else {
            fieldObjects[idx].value = fieldValue;
            setScreenFields((prev) => {
               prev[fieldName] = fieldValue;
               return prev;
            });
         }
      }
      setRefreshscreen(!refreshScreen);
   };

   const onStockStatusSortChange = (field, direction) => {
      if (field !== stockStatusSortField) {
         setScreenFields(
            {
               ...screenFields,
               sortField: field,
               sortDirection: "asc"
            }
         )
         setStockStatusSortField(field);
         setStockStatusSortDirection("asc")
      } else {
         setStockStatusSortDirection(direction);
         setScreenFields(
            {
               ...screenFields,
               sortDirection: direction
            }
         )
      }
   };

   return (
      <DesktopContainer screenName={"Inventory Stock Status"}>
         <div className="filter-header stick-top mb-3">
            <Fragment>
               {fieldsComplete && _.map(fieldObjects, (x, index) => (
                  <DynamicInput
                     key={`${index}-abc`}
                     obj={x}
                     idx={index}
                     onChangeCallback={onChange}
                  />
               ))}
               <Row className="my-3">
                  <Col xs="3"></Col>
                  <Col>
                     {_.map(buttonFunctions, (btn, index) => (
                        < Fragment key={`button-${index}`}>
                           <Button className={"shredButton mx-3"} onClick={btn.function} disabled={btn.disable}>
                              {btn.name}
                           </Button>
                        </Fragment>
                     ))}
                  </Col>
               </Row>
            </Fragment>
         </div>
         <ServiceThreadSpinner on={!loaded}>
            {gridData?.length > 0 ?
               <>
                  <StockStatusGrid
                     data={gridData}
                     criteria={screenFields}
                     sortCallback={onStockStatusSortChange}
                     sortField={stockStatusSortField}
                     sortDirection={stockStatusSortDirection}
                     mode={gridMode}
                     onUpdateRow={() => setScreenFields({ ...screenFields })}
                     exportXlsxCallback={exportXlsx}
                  />
                  <div>
                     <Row className="my-3">
                        <Col xs="6">
                           <Table size="sm" className="data-table">
                              <thead className="table-header">
                                 <tr>
                                    <th width="30%">Classification</th>
                                    <th width="30%">Class Group</th>
                                    <th width="30%">Amount</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {_.map(classificationTotals, (x, index) => {
                                    return (
                                       <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                          <td className="data-cell"></td>
                                          <td className="data-cell">{x.label}</td>
                                          <td className="data-cell">{helpers.formatCurrency(x.value)}</td>
                                       </tr>
                                    )
                                 })}
                              </tbody>
                              <tfoot className="table-footer">
                                 <tr>
                                    <td className="data-cell"></td>
                                    <td className="data-cell"></td>
                                    <td className="data-cell" style={{ fontWeight: "bold" }}>{helpers.formatCurrency(classificationTotal)}</td>
                                 </tr>
                              </tfoot>
                           </Table>
                        </Col>
                     </Row>
                     <Row className="my-3">
                        <Col xs="6">
                           <Table size="sm" className="data-table">
                              <thead className="table-header">
                                 <tr>
                                    <th width="30%">Report Totals</th>
                                    <th width="30%">Location</th>
                                    <th width="30%">Amount</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {_.map(reportTotals, (x, index) => {
                                    return (
                                       <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                          <td className="data-cell"></td>
                                          <td className="data-cell">{x.label}</td>
                                          <td className="data-cell">{helpers.formatCurrency(x.value)}</td>
                                       </tr>
                                    )
                                 })}
                              </tbody>
                              <tfoot className="table-footer">
                                 <tr>
                                    <td className="data-cell"></td>
                                    <td className="data-cell"></td>
                                    <td className="data-cell" style={{ fontWeight: "bold" }}>{helpers.formatCurrency(reportTotal)}</td>
                                 </tr>
                              </tfoot>
                           </Table>
                        </Col>
                     </Row>
                  </div>
               </>
               : (<div className="h5 text-center">
                  {"Please select one item or provide a description"}
               </div>)}
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}